import { isPageNode, NavigationMapItem, NavigationMapItemPermissionsType } from '@lumapps/navigation/types';
import { toCompatibleLanguage, type TranslatableObject } from '@lumapps/translations';

export const nodeIsHidden = (nodePermission?: NavigationMapItemPermissionsType) => nodePermission === 'none';

/**
 *  Display Edit Action if:
 *  - no permission, OR
 *  - permission is set to 'edit' or 'all'
 */
export const nodeIsEditable = (nodePermission?: NavigationMapItemPermissionsType) =>
    nodePermission === undefined || [undefined, 'edit', 'all'].includes(nodePermission);

/**
 *  Display Delete Action if:
 *  - no nodePermission, OR
 *  - permission is set to 'all'
 */
export const nodeIsDeletable = (nodePermission?: NavigationMapItemPermissionsType) =>
    [undefined, 'all'].includes(nodePermission);
/**
 *  Display Move Action if:
 *  - no nodePermission, OR
 *  - permission is set to 'all'
 */
export const nodeIsMovable = (nodePermission?: NavigationMapItemPermissionsType) =>
    [undefined, 'all'].includes(nodePermission);

/**
 * Get the navigation map item title object to use.
 * If the nodeTitle has a value in the given language, use it.
 * If not, use title
 */
export const getNavigationMapNodeTitle = (
    node: Pick<NavigationMapItem, 'type' | 'title' | 'nodeTitle'>,
    language?: string,
) => {
    /* If language is set, do the test relative to the given language. We only return node title if the node title has a
     * language defined in this language
     */
    if (language) {
        if (node.nodeTitle?.translations[toCompatibleLanguage(language)]) {
            return node.nodeTitle;
        }

        if (node.title?.translations[toCompatibleLanguage(language)]) {
            return node.title;
        }
    }

    // Page nodes have a different fallback logic, this avoids title mismatch between the admin and actual navigation
    if (isPageNode(node)) {
        return {
            ...node.title,
            translations: { ...node.nodeTitle?.translations, ...node.title?.translations },
        } as TranslatableObject;
    }

    /** If language is not set, check the the nodeTitle value to assert whether we should return the nodeTitle or title. */
    return node.nodeTitle?.value ? node.nodeTitle : node.title;
};

/**
 * Returns a list of nodes filtered by the provided list of ids
 * @param nodes
 * @param nodesToFilter
 * @returns NavigationMapItem[]
 */
export const filterNavigationMapByIds = (nodes: NavigationMapItem[], nodesToFilter: string[]): NavigationMapItem[] => {
    const filteredNodes = nodes
        .map((node) => {
            // Recursively filter the children
            const filteredChildren = node.children ? filterNavigationMapByIds(node.children, nodesToFilter) : [];

            // Check if the current node should be included (either its id is in the ids list or it has filtered children)
            if (nodesToFilter.includes(node.id as string) || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: filteredChildren.length === 0 ? undefined : filteredChildren, // Keep only the filtered children
                };
            }

            return null; // Return null if the node and its children don't match
        })
        .filter((node) => node !== null); // Remove null nodes

    return filteredNodes as NavigationMapItem[];
};
